import React, { useMemo } from 'react';
import { Empty } from 'antd';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { getChartColors } from '@/utils/chartColors';
import { getChartThemeColors, getChartPluginTheme } from '@/utils/chartTheme';
import useTheme, { resolveTheme } from '@/hooks/stores/useTheme';

ChartJS.register(ArcElement, Tooltip, Legend);

const PieChart = ({ dashboardData }) => {
  const themeMode = useTheme((s) => s.theme);
  const resolvedTheme = resolveTheme(themeMode);
  const chartTheme = useMemo(() => getChartThemeColors(), [resolvedTheme]);
  const pluginTheme = useMemo(
    () => getChartPluginTheme(chartTheme),
    [chartTheme],
  );

  const languageData =
    dashboardData?.field_data?.['Languages spoken']?.map((item) => ({
      type: item.language,
      value: item.candidate_count,
    })) || [];

  const testData = [
    { type: 'English', value: 100 },
    { type: 'Spanish', value: 80 },
    { type: 'French', value: 60 },
    { type: 'German', value: 40 },
  ];

  const dataToUse = languageData.length > 0 ? languageData : testData;
  const colors = useMemo(
    () => getChartColors(dataToUse.length),
    [dataToUse.length, resolvedTheme],
  );

  const chartData = {
    labels: dataToUse.map((item) => item.type),
    datasets: [
      {
        data: dataToUse.map((item) => item.value),
        backgroundColor: colors,
        borderColor: chartTheme.card,
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          ...pluginTheme.legend.labels,
          padding: 20,
          usePointStyle: true,
          generateLabels: (chart) => {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label, i) => {
                const dataset = data.datasets[0];
                const value = dataset.data[i];
                const total = dataset.data.reduce((sum, val) => sum + val, 0);
                const percentage = ((value / total) * 100).toFixed(1);

                return {
                  text: `${label} (${percentage}%)`,
                  fillStyle: dataset.backgroundColor[i],
                  strokeStyle: chartTheme.card,
                  fontColor: chartTheme.foreground,
                  lineWidth: 1,
                  pointStyle: 'circle',
                  hidden: false,
                  index: i,
                };
              });
            }
            return [];
          },
        },
      },
      tooltip: {
        ...pluginTheme.tooltip,
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed;
            const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  try {
    return (
      <div className="bg-card rounded-lg shadow-md border border-border p-6">
        <h3 className="text-xl font-semibold text-foreground mb-4">
          Top 10 Spoken Languages Of Candidates
          {languageData.length === 0 && (
            <span className="text-sm text-muted-foreground ml-2">(Test Data)</span>
          )}
        </h3>

        <div style={{ height: '400px' }}>
          {dataToUse.length > 0 ? (
            <Pie key={`pie-${resolvedTheme}`} data={chartData} options={options} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <Empty description="No data available" />
            </div>
          )}
        </div>
      </div>
    );
  } catch (error) {
    console.error('PieChart - Error rendering chart:', error);
    return (
      <div className="bg-card rounded-lg shadow-md border border-border p-6">
        <h3 className="text-xl font-semibold text-foreground mb-4">
          Top 10 Spoken Languages Of Candidates
        </h3>
        <Empty description={`Error rendering chart: ${error.message}`} />
      </div>
    );
  }
};

export default PieChart;
